<template>
  <div class="d-flex flex-column fill-height">
    <v-toolbar style="max-height: 48px !important" flat dense dark color="grey darken-4">
      <v-toolbar-title class="font-weight-bold">Gruppen</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn v-if="hasCreateRight" icon color="primary" @click="$emit('open-create-dialog')">
        <v-icon>mdi-plus</v-icon>
      </v-btn>
    </v-toolbar>

    <v-progress-linear indeterminate v-if="isLoadingGroups" color="primary"></v-progress-linear>

    <div class="group-list-container flex-grow-1 overflow-y-auto pa-2">
      <div v-if="!isLoadingGroups && (!chatGroups || chatGroups.length === 0)" class="text-center grey--text pa-8">
        <v-icon size="60" color="grey darken-2">mdi-chat-off-outline</v-icon>
        <p class="mt-4 headline">Keine Gruppen</p>
        <p class="body-1">Erstelle eine neue Gruppe, um zu chatten.</p>
      </div>

      <v-card
        v-for="(group, index) in chatGroups"
        :key="group ? group._id : 'empty-' + index"
        v-if="group"
        @click="selectGroup(group._id)"
        dark
        rounded="lg"
        class="mb-2 glass-card"
        :class="{ 'active-group-card': group._id === activeGroupId }"
        elevation="0">
        <v-list-item class="pa-2">
          <v-badge :value="isAnyoneOnline(group)" color="primary" dot bordered overlap offset-x="10" offset-y="10" class="mr-3">
            <v-list-item-avatar color="grey darken-2">
              <v-img v-if="group.bild" :src="group.bild" alt="Gruppenbild"></v-img>
                        <v-icon v-else dark>mdi-account-group</v-icon>          
            </v-list-item-avatar>
          </v-badge>

          <v-list-item-content>
            <v-list-item-title v-text="group.name" class="font-weight-bold"></v-list-item-title>

            <v-list-item-subtitle class="text-truncate" :class="{ 'typing-indicator-text': getTypingData(group).isTyping }">
              {{ getSubtitle(group) }}
            </v-list-item-subtitle>
          </v-list-item-content>

          <v-list-item-action>
                                   
            <v-list-item-action-text class="caption mb-1">
                            {{ formatLastMessageTime(group.lastMessage && group.lastMessage.createdAt) }}            
            </v-list-item-action-text>

                                   
            <v-badge :value="group.hasUnread" color="primary" dot inline v-if="group.hasUnread">
              <v-icon v-if="group.isMentioned" small color="primary lighten-1" style="margin-top: -3px"> mdi-at </v-icon>
            </v-badge>
                     
          </v-list-item-action>
        </v-list-item>
      </v-card>
    </div>
  </div>
</template>

<script>
// Importiere 'mapState' für 'user' und 'typingUsers'
import { mapState, mapActions, mapGetters } from "vuex"

export default {
  name: "ChatGroupList",
  props: {
    hasCreateRight: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    // Holt den 'user' (um "Du:" zu sagen)
    ...mapState("userStore", ["user"]),

    // Holt die 'typingUsers' (um "tippt..." zu sagen)
    ...mapState("chatStore", [
      "chatGroups",
      "activeGroupId",
      "isLoadingGroups",
      "typingUsers", // <-- NEU
    ]),

    ...mapGetters("chatStore", ["getPresenceByUserId"]),
  },

  methods: {
    ...mapActions("chatStore", {
      selectGroup: "setActiveGroup",
    }),

    isAnyoneOnline(group) {
      if (!group || !group.members) return false

      // Prüft, ob irgendein Mitglied...
      return group.members.some((member) => {
        if (!member.userId) return false

        // ...dessen Status im Store als "online" vermerkt ist.
        const presence = this.getPresenceByUserId(member.userId._id)
        return presence && presence.isOnline
      })
    },

    /**
     * Formatiert den Zeitstempel rechts in der Liste
     * (z.B. "16:05", "Gestern", "03.11.25")
     */ formatLastMessageTime(timestamp) {
      if (!timestamp) return ""
      const date = new Date(timestamp)
      const today = new Date()
      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1) // 1. Heute -> Nur Uhrzeit

      if (date.toDateString() === today.toDateString()) {
        return date.toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" })
      } // 2. Gestern -> "Gestern"
      if (date.toDateString() === yesterday.toDateString()) {
        return "Gestern"
      } // 3. Älter -> Datum
      return date.toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "2-digit" })
    },
    /**
     * NEU: Helfer, der die Tipper-Info holt (ohne "dich")
     */
    getTypingData(group) {
      if (!this.typingUsers || !this.user || !group.members) {
        return { isTyping: false, text: "" }
      }

      // 1. Hol die IDs der Tipper in DIESER Gruppe
      const typingIds = this.typingUsers[group._id] || []

      // 2. Finde die vollen User-Objekte (außer mir)
      const tippers = group.members.filter((member) => typingIds.includes(member.userId._id) && member.userId._id !== this.user._id)

      const count = tippers.length
      if (count === 0) return { isTyping: false, text: "" }

      // 3. Baue den Text
      let text = ""
      if (count === 1) text = `${tippers[0].userId.vorname} schreibt...`
      else if (count === 2) text = `${tippers[0].userId.vorname} und ${tippers[1].userId.vorname} schreiben...`
      else text = "Mehrere Personen schreiben..."

      return { isTyping: true, text: text }
    },

    /**
     * "Richtig Geil"-Untertitel (kombiniert alles)
     */
    getSubtitle(group) {
      // 1. PRIORITÄT: Jemand tippt
      const typingData = this.getTypingData(group)
      if (typingData.isTyping) {
        return typingData.text
      }

      // 2. PRIORITÄT: Letzte Nachricht
      if (group.lastMessage && group.lastMessage.textContent) {
        const sender = group.lastMessage.senderId
        const text = group.lastMessage.textContent

        if (sender && this.user && sender._id === this.user._id) {
          return `Du: ${text}`
        }
        if (sender && sender.vorname) {
          return `${sender.vorname}: ${text}`
        }
        return text
      }

      // 3. PRIORITÄT: Fallback auf Mitglieder
      const memberCount = group.members ? group.members.length : 0
      return `${memberCount} Mitglieder`
    },
  },
}
</script>

<style lang="scss" scoped>
/* Stellt sicher, dass die Liste scrollt, nicht der Header */
.group-list-container {
  height: 0; /* Flexbox-Trick */
}

/* DER "RICHTIG GEIL"-STYLE: GLASSMORPHISM & GLOW
*/
.glass-card {
  /* 1. Das "Glas"-Design */
  background-color: rgba(255, 255, 255, 0.03) !important; /* leichter Schimmer */
  border: 1px solid rgba(255, 255, 255, 0.05) !important;
  transition: all 0.2s ease-out;

  /* 2. Hover-Effekt (für Desktop) */
  @media (hover: hover) {
    &:hover {
      background-color: rgba(255, 255, 255, 0.08) !important;
      transform: translateY(-1px);
    }
  }
}

/* 3. Der "Glow"-Effekt für die aktive Gruppe */
.active-group-card {
  background-color: rgba(var(--v-primary-base-rgb), 0.1) !important;
  border-color: rgba(var(--v-primary-base-rgb), 0.7) !important;

  /* Der "Glow" */
  box-shadow: 0 0 15px -5px rgba(var(--v-primary-base-rgb), 0.4);
}

/* 4. Der "Tippt..."-Text-Stil */
.typing-indicator-text {
  color: var(--v-primary-base) !important;
  font-weight: 600;
  font-style: italic;
}

/* Aktives Item in der Liste (Fallback) */
.v-list-item-group ::v-deep .v-list-item--active {
  background-color: rgba(var(--v-primary-base-rgb), 0.2);
}
</style>
